import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import properties from '../../core-utils/properties';
import {
  DocumentDetailsDataType,
  formatTermCompilerData,
  LocationState,
  TermCompilerContextType,
  TermCompilerDataType,
} from '../../core-utils/helper-utils/termCompilerHelper';

export const TermCompilerContext = createContext<
  TermCompilerContextType | undefined
>(undefined);

export const TermCompilerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const { term_compiler_id } = location.state as LocationState;
  const abortController = new AbortController();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [openDocument, setOpenDocument] = useState<boolean>(false);
  const [
    documentDetails,
    setDocumentDetails,
  ] = useState<DocumentDetailsDataType>({
    documentUrl: '',
    chunks: [],
    pageNumber: 0,
  });
  const [documents, setDocuments] = useState<any[]>([]);
  const [
    termCompilerData,
    setTermCompilerData,
  ] = useState<TermCompilerDataType>({
    term_compiler_id: '',
    compiler_name: '',
    date_created: '',
    selected_terms: [],
    status: '',
    eg_nid: '',
  });

  const getTermCompilerDetails = async () => {
    setTableLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getTermCompilerMetaData,
        { term_compiler_id },
        { signal: abortController.signal },
      )
      .then((response) => {
        const formattedData: TermCompilerDataType = formatTermCompilerData(
          response.data?.data?.termCompilerData,
        );
        setTermCompilerData(formattedData);
        setDocuments(response.data?.data?.documents);
        setTableLoading(false);
      });
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.allSettled([getTermCompilerDetails()]).then(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    apiCalls();
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <TermCompilerContext.Provider
      value={{
        loading,
        tableLoading,
        termCompilerData,
        filters,
        setFilters,
        openDocument,
        setOpenDocument,
        documentDetails,
        setDocumentDetails,
        documents,
        setDocuments,
      }}
    >
      {children}
    </TermCompilerContext.Provider>
  );
};

// Create a custom hook for safer consumption
export const useTermCompilerContext = () => {
  const context = useContext(TermCompilerContext);
  if (context === undefined) {
    throw new Error(
      'useTermCompilerContext must be used within a TermCompilerProvider',
    );
  }
  return context;
};
